import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import { toast } from "react-toastify";
import {
  formattedDate,
  formattedTime,
} from "../../components/formatted/FormattedDateAndTime";
import EmptyState from "../../components/EmptyState";
import { InlineLoader } from "../../components/loader/CharacterizationLoader";
import Pagination from "../../components/utils/Pagination";
import StandardTable from "../../ui/components/StandardTable";
import { getSettingsChangeHistory } from "../../services/settingsChangeHistory";

const formatEventAt = (at, t) =>
  at ? `${formattedDate(at, t, true, false, true)} ${formattedTime(at)}` : "—";

const formatChangeValue = (field, value, t) => {
  if (value == null || value === "") return "—";
  if (field === "password" && value === "changed") {
    return t("settingsHistory.passwordChanged");
  }
  if (typeof value === "boolean") return value ? t("Yes") : t("No");
  return String(value);
};

const flattenHistoryRows = (entries) => {
  if (!Array.isArray(entries)) return [];
  const rows = [];
  entries.forEach((entry) => {
    const changes = Array.isArray(entry.changes) ? entry.changes : [];
    if (changes.length === 0) {
      rows.push({
        id: `${entry.id}-action`,
        at: entry.at,
        actorName: entry.actorName,
        action: entry.action,
        field: null,
        before: null,
        after: null,
      });
      return;
    }
    changes.forEach((change, index) => {
      rows.push({
        id: `${entry.id}-${change.field}-${index}`,
        at: entry.at,
        actorName: entry.actorName,
        action: entry.action,
        field: change.field,
        before: change.before,
        after: change.after,
      });
    });
  });
  return rows;
};

const SettingsChangeHistoryPanel = ({ entityType, entityId }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);

  useEffect(() => {
    if (!entityId) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setCurrentPage(1);

    getSettingsChangeHistory(entityType, entityId)
      .then((response) => {
        if (cancelled) return;
        setEntries(response?.data?.entries || []);
      })
      .catch((error) => {
        console.error("getSettingsChangeHistory failed:", error);
        if (!cancelled) {
          toast.error(t("settingsHistory.loadError"), {
            className: "custom-toast",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, t]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordPerPage]);

  const rows = useMemo(() => flattenHistoryRows(entries), [entries]);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * recordPerPage;
    return rows.slice(start, start + recordPerPage);
  }, [rows, currentPage, recordPerPage]);

  const showListEmpty = !isLoading && rows.length === 0;

  return (
    <div className="settings-list-shell w-full">
      <Card className="table border-0 shadow-none bg-white rounded-lg">
        <StandardTable
          fluidColCount={showListEmpty ? 1 : 5}
          fixedColWidth={0}
          containerClassName={
            showListEmpty ? "table-standard-container--list-empty" : undefined
          }
          columnAlignments={
            showListEmpty ? ["left"] : ["left", "left", "left", "left", "left"]
          }
          header={
            showListEmpty ? null : (
              <div className="table-standard-header">
                <div className="table-standard-header-cell">{t("Date")}</div>
                <div className="table-standard-header-cell">
                  {t("settingsHistory.columnAuthor")}
                </div>
                <div className="table-standard-header-cell">
                  {t("settingsHistory.columnField")}
                </div>
                <div className="table-standard-header-cell">
                  {t("settingsHistory.columnBefore")}
                </div>
                <div className="table-standard-header-cell">
                  {t("settingsHistory.columnAfter")}
                </div>
              </div>
            )
          }
          bottom={
            rows.length > 0 ? (
              <div className="table-standard-bottom table-pagination-bottom">
                <Pagination
                  totalEntry={rows.length}
                  recordPerPage={recordPerPage}
                  setRecordPerPage={setRecordPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  resultPage={resultPage}
                  setResultPage={setResultPage}
                />
              </div>
            ) : null
          }
        >
          {isLoading ? (
            <div className="table-standard-row">
              <div
                className="table-standard-row-cell justify-center min-h-[200px]"
                style={{ gridColumn: "1 / -1" }}
              >
                <InlineLoader height="200px" />
              </div>
            </div>
          ) : paginatedRows.length > 0 ? (
            paginatedRows.map((row) => (
              <div key={row.id} className="table-standard-row">
                <div className="table-standard-row-cell">
                  {formatEventAt(row.at, t)}
                </div>
                <div className="table-standard-row-cell">
                  {row.actorName || "—"}
                </div>
                <div className="table-standard-row-cell">
                  {row.field
                    ? t(`settingsHistory.field.${row.field}`, {
                        defaultValue: row.field,
                      })
                    : t(`settingsHistory.action.${row.action}`)}
                </div>
                <div className="table-standard-row-cell">
                  <span className="table-standard-row-cell-truncate">
                    {formatChangeValue(row.field, row.before, t)}
                  </span>
                </div>
                <div className="table-standard-row-cell">
                  <span className="table-standard-row-cell-truncate">
                    {formatChangeValue(row.field, row.after, t)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <EmptyState
              title={t("settingsHistory.empty")}
              description={t("settingsHistory.emptyHint")}
            />
          )}
        </StandardTable>
      </Card>
    </div>
  );
};

export default SettingsChangeHistoryPanel;
